import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { PageSkeleton } from './Skeleton';

/**
 * 私有路由组件 - 需要登录才能访问
 * @param {Object} props
 * @param {React.ReactNode} props.children - 子组件
 * @param {boolean} [props.requireAdmin=false] - 是否需要管理员权限
 * @param {boolean} [props.requireNormalUser=false] - 是否仅普通用户可访问
 */
const PrivateRoute = ({ children, requireAdmin = false, requireNormalUser = false }) => {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();
  const location = useLocation();

  // 显示加载状态
  if (isLoading) {
    return <PageSkeleton />;
  }

  // 未登录，重定向到登录页
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // 需要管理员权限但当前用户不是管理员
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // 仅普通用户可访问但当前用户是管理员
  if (requireNormalUser && isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

/**
 * 公开路由组件 - 已登录用户重定向
 * @param {Object} props
 * @param {React.ReactNode} props.children - 子组件
 */
export const PublicRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();

  // 显示加载状态
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-love-pink border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  // 已登录，重定向到对应页面
  if (isAuthenticated) {
    const target = isAdmin ? '/admin' : '/dashboard';
    return <Navigate to={target} replace />;
  }

  return children;
};

export default PrivateRoute;
